import React, { FC, useCallback, useRef } from 'react'
import { ArrowUpOutlined } from '@ant-design/icons'
import { alpha, Box, CircularProgress, IconButton, useTheme } from '@mui/material'
import { TextField } from 'components/@extended/TextField'

interface SmartImportChatInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  isLoading?: boolean
  placeholder?: string
  disabled?: boolean
  minRows?: number
  maxRows?: number
  autoFocus?: boolean
}

const SmartImportChatInput: FC<SmartImportChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  placeholder = 'Message the copilot...',
  disabled = false,
  minRows = 1,
  maxRows = 6,
  autoFocus = false,
}) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const canSend = !!value.trim() && !disabled && !isLoading

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled || isLoading) return
    onSubmit(trimmed)
    inputRef.current?.focus()
  }, [value, disabled, isLoading, onSubmit])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '6px 8px 6px 16px',
          borderRadius: 999,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          minHeight: 44,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      >
        <TextField
          fullWidth
          autoFocus={autoFocus}
          disabled={disabled}
          multiline
          minRows={minRows}
          maxRows={maxRows}
          variant="standard"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          inputRef={inputRef}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: 16,
              lineHeight: 1.6,
              padding: 0,
              '& textarea': {
                padding: 0,
              },
            },
          }}
          inputProps={{
            onKeyDown: handleKeyDown,
          }}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              padding: 0,
            },
            '& .MuiInputBase-input': {
              padding: 0,
            },
          }}
        />
        <IconButton
          onClick={handleSubmit}
          disabled={!canSend}
          aria-label="Send message"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            aspectRatio: '1 / 1',
            backgroundColor: canSend ? theme.palette.primary.main : theme.palette.action.disabledBackground,
            color: canSend ? theme.palette.primary.contrastText : theme.palette.text.disabled,
            transition: 'transform 0.15s ease, background-color 0.15s ease',
            '&:hover': {
              backgroundColor: canSend ? theme.palette.primary.dark : theme.palette.action.disabledBackground,
            },
          }}
        >
          {isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowUpOutlined />}
        </IconButton>
      </Box>
    </Box>
  )
}

export default SmartImportChatInput
